import { Box, Link, Tooltip, Typography } from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import Student from '../../model/Student';
import CopyIconButton from './CopyIconButton';
import TeacherContacts from './TeacherContacts';

type Props = {
	student: Student;
	showTeacherContacts?: boolean;
};

const ContactLinks = ({ student, showTeacherContacts = true }: Props) => {
	const email = student.email ? student.email.trim() : '';
	const parentEmail = student.parentEmail ? student.parentEmail.trim() : '';
	const phoneRaw = student.phone ? student.phone.trim() : '';
	const phoneDigits = phoneRaw.replace(/\D+/g, '');
	const phone =
		phoneDigits.length === 9
			? `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`
			: phoneRaw;
	const combinedEmails = email && parentEmail && email !== parentEmail ? `${email},${parentEmail}` : '';
	const hasContact = Boolean(email || parentEmail || phone);

	const renderEmailRow = (label: string, value: string) => {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					{label}
				</Typography>
				<Link
					href={`mailto:${value}`}
					underline='hover'
					variant='caption'
				>
					{value}
				</Link>
				<CopyIconButton
					value={value}
					ariaLabel='Skopiuj email'
				/>
			</Box>
		);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
			{email && renderEmailRow('Do ucznia:', email)}
			{parentEmail && renderEmailRow('Do rodzica:', parentEmail)}
			{combinedEmails && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
					<Typography
						variant='caption'
						fontWeight='medium'
					>
						Do ucznia i rodzica:
					</Typography>
					<Tooltip title={combinedEmails}>
						<Link
							href={`mailto:${combinedEmails}`}
							underline='hover'
							variant='caption'
						>
							razem
						</Link>
					</Tooltip>
					<CopyIconButton
						value={combinedEmails}
						ariaLabel='Skopiuj maile ucznia i rodzica'
					/>
				</Box>
			)}
			{phone && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
					<Typography
						variant='caption'
						fontWeight='medium'
					>
						Telefon:
					</Typography>
					<Link
						href={`tel:${phoneDigits || phoneRaw}`}
						underline='hover'
						variant='caption'
					>
						{phone}
					</Link>
					<CopyIconButton
						value={phoneDigits || phoneRaw}
						ariaLabel='Skopiuj telefon'
					/>
				</Box>
			)}
			{showTeacherContacts && student.teachers?.length > 0 && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
					<Typography
						variant='caption'
						fontWeight='medium'
					>
						{student.teachers.length > 1 ? 'Do nauczycieli:' : 'Do nauczyciela:'}
					</Typography>
					<Tooltip
						placement='top'
						componentsProps={{
							tooltip: {
								sx: {
									backgroundColor: 'transparent',
									p: 0,
								},
							},
						}}
						title={
							<TeacherContacts
								teachers={student.teachers}
								minWidth={240}
							/>
						}
					>
						<Box sx={{ display: 'inline-flex' }}>
							<ContactMailIcon sx={{ fontSize: 16, cursor: 'pointer' }} />
						</Box>
					</Tooltip>
				</Box>
			)}
			{!hasContact && (
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					—
				</Typography>
			)}
		</Box>
	);
};

export default ContactLinks;
